import * as THREE from "three";
import { Renderer } from "./engine/renderer";
import { SceneLoader } from "./engine/loader/sceneLoader";
import { SpatialLoader, type CullCamera } from "./engine/loader/spatialLoader";
import { detectGpuCapacity } from "./engine/gpuTier";
import type { GsplatTransport } from "./transport/types";

export interface GsplatLayerStats {
  splatCount: number;       // total resident splats on GPU
  resident: number;         // # blocks currently on GPU
  inView: number;           // # nodes intersecting frustum (spatial mode only; 0 in scene mode)
  inFlight: number;         // # blocks being fetched
}

export interface GsplatLayerOptions {
  jobId: string;            // identity for cache key + layer name
  transport: GsplatTransport;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** Object3D parent under which the layer is attached. Defaults to `scene`. */
  parent?: THREE.Object3D;
  /** Render order — higher = drawn later. Default 1000 (after pointcloud/IFC). */
  renderOrder?: number;
  /**
   * Override the splat budget. When unset the adapter takes it from the form
   * factor — desktop 7M, mobile 3M, see `engine/gpuTier.ts`. Either way the
   * pool is then capped to the capture's own splat count.
   */
  gpuCapacitySplats?: number;
  /** Throttle for spatial loader cull updates (ms). Default 100. */
  spatialUpdateMs?: number;
  onStats?: (s: GsplatLayerStats) => void;
  onLoadError?: (err: { stage: string; message: string }) => void;
  /**
   * Request the host to render a frame. The viewer's render loop is
   * on-demand (gated by `_renderLoopCount`); while this layer is mounted the
   * adapter calls this every animation frame so the splat streams in, stays
   * visible, and re-sorts as the camera moves without the user interacting.
   * Wire it to `viewer.updateRenderer` (same hook the viewer uses for
   * `GaspManager.onNeedRender`).
   */
  onRenderNeeded?: () => void;
}

export interface GsplatLayerHandle {
  /** Detach from scene, free GL resources, abort pending fetches. */
  dispose(): void;
  /** Force the spatial loader to re-cull next frame (e.g. after camera teleport). */
  invalidate(): void;
}

// Each splat consumes 64 B in the data texture, so capacity directly drives
// VRAM use (2M ≈ 128 MB). The adapter picks a per-tier default from
// `detectGpuTier()` — low/mid/high → 1M/1.5M/2M. When the spatial loader
// exceeds this budget the LRU eviction policy in renderer.addNode frees the
// oldest (out-of-view) blocks first, so distant detail naturally fades out.
const DEFAULT_SPATIAL_UPDATE_MS = 100;
const DEFAULT_RENDER_ORDER = 1000;

/**
 * Mount a Gaussian-Splat layer into an existing Three.js scene.
 *
 * The layer renders splats with a custom WebGL2 program that shares the
 * Three.js renderer's GL context. It hooks into Three.js's per-mesh
 * `onBeforeRender` so the splat draw happens during Three.js's normal pass
 * (not as a separate render call), letting splats compose correctly with
 * the host renderer's clear, viewport, and other passes.
 *
 * Caller must `dispose()` on unmount.
 */
export function attachGsplatLayer(opts: GsplatLayerOptions): GsplatLayerHandle {
  const gl = opts.renderer.getContext() as WebGL2RenderingContext;
  if (!gl || gl.getParameter(gl.VERSION).toString().indexOf("WebGL 2.0") < 0) {
    throw new Error(
      "GsplatLayer requires a WebGL2 context. Three.js renderer was created with WebGL1.",
    );
  }

  // Sentinel Object3D — the splat draw piggy-backs on its onBeforeRender.
  // We set frustumCulled=false so Three.js never skips it when the host
  // camera frustum doesn't intersect (the splat layer manages its own
  // culling at the block level).
  const layerObj = new THREE.Object3D();
  layerObj.name = `gsplat-layer:${opts.jobId}`;
  layerObj.frustumCulled = false;
  layerObj.renderOrder = opts.renderOrder ?? DEFAULT_RENDER_ORDER;

  // Renderer + loaders are built asynchronously: GPU tier detection
  // (detect-gpu) needs to fetch its benchmark JSON. Until both this and the
  // meta load finish, onBeforeRender is a no-op.
  let splatRenderer: Renderer | null = null;
  let mode: "scene" | "spatial" | null = null;
  let sceneLoader: SceneLoader | null = null;
  let spatialLoader: SpatialLoader | null = null;
  let lastSpatialUpdateAt = 0;
  let stats: GsplatLayerStats = { splatCount: 0, resident: 0, inView: 0, inFlight: 0 };
  let disposed = false;
  let invalidated = true;
  // World-space AABB of the splat, from meta. Used to refit the ortho camera
  // near/far per-draw so the splat isn't depth-clipped (see onBeforeRender).
  let aabbMin: number[] | null = null;
  let aabbMax: number[] | null = null;

  const stateThrottle = opts.spatialUpdateMs ?? DEFAULT_SPATIAL_UPDATE_MS;
  const cacheKey = opts.jobId;

  void (async () => {
    let capacity: number;
    if (opts.gpuCapacitySplats != null) {
      capacity = opts.gpuCapacitySplats;
      console.log(`[gsplat] adapter: gpu capacity ${capacity} (caller override)`);
    } else {
      const t = await detectGpuCapacity(gl);
      capacity = t.capacitySplats;
      if (t.result) {
        console.log(
          `[gsplat] adapter: capacity=${capacity} (${t.isMobile ? "mobile" : "desktop"}) · detect-gpu tier=${t.result.tier} type=${t.result.type} fps=${t.result.fps ?? "?"} gpu="${t.result.gpu ?? ""}"`,
        );
      } else {
        console.warn(
          `[gsplat] adapter: detect-gpu failed, form factor from user agent → ${t.isMobile ? "mobile" : "desktop"}, capacity=${capacity}`,
          t.error,
        );
      }
    }
    if (disposed) return;
    splatRenderer = new Renderer(gl, capacity);

    console.log(`[gsplat] adapter: fetching meta for jobId=${opts.jobId}`);
    const meta = await opts.transport.getMeta();
    if (disposed) return;
    aabbMin = Array.from(meta.header.aabbMin as ArrayLike<number>);
    aabbMax = Array.from(meta.header.aabbMax as ArrayLike<number>);
    console.log(
      `[gsplat] adapter: meta loaded — magic=${meta.header.magic} version=${meta.header.version} splatCount=${meta.header.splatCount} blockCount=${meta.header.blockCount} nodeCount=${meta.header.nodeCount} aabb=[${meta.header.aabbMin.join(",")}]→[${meta.header.aabbMax.join(",")}]`,
    );

    if (meta.header.blockCount === 1) {
      mode = "scene";
      console.log(`[gsplat] adapter: single-block → SceneLoader`);
      const r = splatRenderer;
      sceneLoader = new SceneLoader(cacheKey, opts.transport, r, {
        onProgress: (p) => {
          stats = {
            splatCount: r.splatCount(),
            resident: r.activeSlots(),
            inView: 0,
            inFlight: 0,
          };
          opts.onStats?.(stats);
          if (p.stage.startsWith("ready")) {
            stats.splatCount = r.splatCount();
            opts.onStats?.(stats);
          }
        },
        onError: (e) => opts.onLoadError?.({ stage: e.stage, message: e.message }),
      });
      // SceneLoader.loadScene() expects this.meta set; inject pre-fetched meta.
      (sceneLoader as unknown as { meta: typeof meta }).meta = meta;
      void sceneLoader.loadScene();
    } else {
      mode = "spatial";
      console.log(
        `[gsplat] adapter: multi-block (${meta.header.blockCount} blocks, ${meta.header.nodeCount} octree nodes) → SpatialLoader`,
      );
      // Estimate per-block splat count → derive a maxResident that fits
      // capacity. The block-count ceiling matters because per-block cost
      // (gzip decode + GPU upload + per-node bookkeeping) scales with
      // block count, not just total splats. Old code clamped to 200,
      // which under-utilised the GPU when blockSize shrank (8K splats →
      // ~3700 avg → 200 blocks = ~745K splats, half the 1.5M capacity).
      const avgPerBlock = meta.header.blockCount > 0
        ? Math.max(1, Math.floor(meta.header.splatCount / meta.header.blockCount))
        : 16_000;
      const fitInCapacity = Math.max(1, Math.floor(capacity / avgPerBlock));
      const maxResident = Math.min(fitInCapacity, meta.header.nodeCount);
      console.log(
        `[gsplat] adapter: capacity=${capacity} avgPerBlock=${avgPerBlock} maxResident=${maxResident}`,
      );
      const r = splatRenderer;
      spatialLoader = new SpatialLoader(cacheKey, opts.transport, r, {
        // CloudFront fronts the bucket via HTTP/2 — single TCP connection,
        // many multiplexed streams. 64 in-flight saturates the edge cache
        // hit path without overflowing per-host browser stream limits.
        concurrency: 64,
        maxResident,
        // The renderer enforces splats, not blocks; tell the loader the same
        // number so it stops asking for blocks that only fit by evicting a
        // block it still wants.
        splatBudget: capacity,
        evictionDelayMs: 2000,
        onProgress: (p) => {
          stats = {
            splatCount: r.splatCount(),
            resident: p.resident,
            inView: p.inView,
            inFlight: p.inFlight,
          };
          opts.onStats?.(stats);
        },
        onError: (e) => opts.onLoadError?.({ stage: e.stage, message: e.message }),
      });
      (spatialLoader as unknown as { meta: typeof meta }).meta = meta;
    }
    invalidated = true;
  })().catch((err) => {
    console.error(`[gsplat] adapter: setup failed`, err);
    opts.onLoadError?.({
      stage: "meta",
      message: err instanceof Error ? err.message : String(err),
    });
  });

  // ── per-frame draw hook ──
  // We attach onBeforeRender to a dummy Mesh because Three.js only invokes
  // onBeforeRender on actual rendered objects. The mesh has zero verts so it
  // contributes nothing visually; we just use its hook as a frame trigger.
  const triggerGeom = new THREE.BufferGeometry();
  triggerGeom.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(0), 3));
  triggerGeom.setDrawRange(0, 0);
  const triggerMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false });
  const triggerMesh = new THREE.Mesh(triggerGeom, triggerMat);
  triggerMesh.frustumCulled = false;
  triggerMesh.renderOrder = layerObj.renderOrder;

  const tmpView = new THREE.Matrix4();
  const tmpProj = new THREE.Matrix4();

  triggerMesh.onBeforeRender = (renderer, _scene, camera) => {
    if (disposed || !splatRenderer) return;

    const c = camera as THREE.Camera & {
      matrixWorldInverse: THREE.Matrix4;
      projectionMatrix: THREE.Matrix4;
    };
    c.updateMatrixWorld();
    tmpView.copy(c.matrixWorldInverse);
    const viewMat = tmpView.elements as unknown as Float32Array;

    // Orthographic depth fit. The camera near/far are fit to the SFM/scene
    // (and in ortho the camera sits near/inside the content), so a splat that
    // extends past that depth range gets clipped — most visibly at the bottom.
    // Refit near/far to the splat AABB just for this draw, then restore, so the
    // rest of the frame is untouched. Ortho allows negative near (no
    // perspective divide). Mirrors DesignManager.computeAdaptiveNearFar.
    const oc = c as unknown as {
      isOrthographicCamera?: boolean;
      near: number;
      far: number;
      updateProjectionMatrix: () => void;
    };
    let projMat = c.projectionMatrix.elements as unknown as Float32Array;
    if (oc.isOrthographicCamera === true && aabbMin && aabbMax) {
      const nf = orthoNearFarForAabb(c, aabbMin, aabbMax);
      if (nf) {
        const savedNear = oc.near;
        const savedFar = oc.far;
        oc.near = nf.near;
        oc.far = nf.far;
        oc.updateProjectionMatrix();
        projMat = tmpProj.copy(c.projectionMatrix).elements as unknown as Float32Array;
        oc.near = savedNear;
        oc.far = savedFar;
        oc.updateProjectionMatrix();
      }
    }

    // Spatial cull throttling.
    if (mode === "spatial" && spatialLoader) {
      const now = performance.now();
      if (invalidated || now - lastSpatialUpdateAt >= stateThrottle) {
        const ev = new THREE.Vector3();
        c.getWorldPosition(ev);
        const cull: CullCamera = {
          eye: [ev.x, ev.y, ev.z],
          viewProj: mat4MulInto(projMat, viewMat),
        };
        spatialLoader.update(cull, viewMat, projMat);
        lastSpatialUpdateAt = now;
        invalidated = false;
      }
    }

    // Save THREE state we touch.
    const prevProgram = gl.getParameter(gl.CURRENT_PROGRAM);
    const prevVAO = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
    const prevTexUnit = gl.getParameter(gl.ACTIVE_TEXTURE);
    const prevTexBinding = gl.getParameter(gl.TEXTURE_BINDING_2D);
    const prevBlendEnabled = gl.isEnabled(gl.BLEND);
    const prevDepthEnabled = gl.isEnabled(gl.DEPTH_TEST);

    const sz = new THREE.Vector2();
    renderer.getDrawingBufferSize(sz);
    splatRenderer.renderFrame(viewMat, projMat, sz.x, sz.y);

    // Restore.
    gl.useProgram(prevProgram);
    gl.bindVertexArray(prevVAO);
    gl.activeTexture(prevTexUnit);
    gl.bindTexture(gl.TEXTURE_2D, prevTexBinding);
    if (prevBlendEnabled) gl.enable(gl.BLEND);
    else gl.disable(gl.BLEND);
    if (prevDepthEnabled) gl.enable(gl.DEPTH_TEST);
    else gl.disable(gl.DEPTH_TEST);
    renderer.resetState();
  };

  layerObj.add(triggerMesh);
  (opts.parent ?? opts.scene).add(layerObj);

  // Render-per-frame pump. The viewer render loop is on-demand (gated by
  // _renderLoopCount); while this layer is mounted we request a frame every
  // rAF so async setup, block streaming, and per-frame depth sorting all show
  // up live, and the splat re-sorts as the camera moves — no user interaction
  // needed. Stops on dispose, so the loop returns to idle once GS is closed.
  let pumpRaf = 0;
  const pump = () => {
    if (disposed) return;
    opts.onRenderNeeded?.();
    pumpRaf = requestAnimationFrame(pump);
  };
  pumpRaf = requestAnimationFrame(pump);

  return {
    invalidate() {
      invalidated = true;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (pumpRaf) cancelAnimationFrame(pumpRaf);
      sceneLoader?.abort();
      spatialLoader?.abort();
      (opts.parent ?? opts.scene).remove(layerObj);
      triggerGeom.dispose();
      triggerMat.dispose();
      splatRenderer?.dispose();
    },
  };
}

// Adaptive orthographic near/far so the splat AABB is never depth-clipped.
// Projects the 8 world-space AABB corners onto the camera view direction
// (signed depth) and fits near/far to them. Orthographic has no perspective
// divide, so "behind the camera plane" (negative near) is valid and kept.
// Mirrors DesignManager.computeAdaptiveNearFar (commit 745d904a2).
const _vdir = new THREE.Vector3();
function orthoNearFarForAabb(
  cam: THREE.Camera,
  aabbMin: number[],
  aabbMax: number[],
): { near: number; far: number } | null {
  const dir = cam.getWorldDirection(_vdir);
  const p = cam.position;
  let minDepth = Infinity;
  let maxDepth = -Infinity;
  for (let i = 0; i < 8; i++) {
    const x = (i & 1 ? aabbMax[0]! : aabbMin[0]!) - p.x;
    const y = (i & 2 ? aabbMax[1]! : aabbMin[1]!) - p.y;
    const z = (i & 4 ? aabbMax[2]! : aabbMin[2]!) - p.z;
    const d = x * dir.x + y * dir.y + z * dir.z;
    if (d < minDepth) minDepth = d;
    if (d > maxDepth) maxDepth = d;
  }
  if (!isFinite(minDepth)) return null;
  const near = minDepth; // negative allowed in ortho
  const far = Math.max(maxDepth, near + 0.01);
  return { near, far };
}

// Column-major matrix multiply, allocates a fresh Float32Array each call.
// Cheap (16 cells) and simpler than threading a scratch buffer through the cull update.
function mat4MulInto(a: Float32Array, b: Float32Array): Float32Array {
  const out = new Float32Array(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += a[k * 4 + row]! * b[col * 4 + k]!;
      out[col * 4 + row] = v;
    }
  }
  return out;
}
